/**
 * 跨标签空间切换通信客户端（SharedWorker 通道）
 *
 * 发送方：spaceManager.switchToSpace 成功后 broadcastSpaceSwitch(新身份id, 团队名)；
 * 接收方：useSpaceSyncGuard 订阅，收到他标签的切换消息后自行比对身份弹提示。
 * 环境无 SharedWorker（如老 Safari）时静默降级为 no-op，不影响主流程。
 */
export interface SpaceSwitchMessage {
  type: 'space_switched';
  /** 发送方标签 id（Worker 会回投发送方，客户端据此自过滤防自己弹自己） */
  from: string;
  /** 切换后的身份 id（团队=teamId，个人=personalUserId；无法确定时 null） */
  identityId: string | null;
  /** 目标团队名（切回个人时为 null） */
  teamName: string | null;
}

const WORKER_NAME = 'amai-space-sync';
/** localStorage 兜底通道 key：SharedWorker 端口失效/进程被回收时广播会静默蒸发，
 *  storage 事件由浏览器原生跨标签投递（写入方自身不触发，天然自过滤），双通道互补 */
const STORAGE_KEY = 'anmo_ai_space_switch';

/** 标签 id：每标签一份（本模块 per-JS-context 单例），Worker 回投时据此识别自己 */
const tabId = Math.random().toString(36).slice(2);

let sharedWorker: SharedWorker | null = null;

const getWorker = (): SharedWorker | null => {
  if (typeof SharedWorker === 'undefined') return null;
  if (!sharedWorker) {
    try {
      sharedWorker = new SharedWorker(new URL('./spaceSync.worker.ts', import.meta.url), {
        type: 'module',
        name: WORKER_NAME,
      });
      // Worker 脚本加载失败/进程异常：不监听则完全无声，跨标签通道断了都没痕迹
      sharedWorker.onerror = error => {
        console.error('[空间同步] SharedWorker 异常:', error.message || error);
      };
      sharedWorker.port.start();
    } catch (error) {
      // 构造失败（CSP/内核限制等）原会直接炸掉调用方 useEffect，守卫被错误边界
      // 整个卸载、跨标签提示静默失效——降级为 no-op 与无 SharedWorker 环境一致
      console.error('[空间同步] SharedWorker 创建失败，本标签不参与跨标签提示:', error);
      return null;
    }
  }
  return sharedWorker;
};

/** 切换成功后调用：把新身份广播给其他标签页 */
export const broadcastSpaceSwitch = (identityId: string | null, teamName: string | null): void => {
  const worker = getWorker();
  if (!worker) {
    console.warn('[空间同步] 发送方无 SharedWorker，广播未发出');
    return;
  }
  // debug（20261030 排查「历史页偶发收不到过期弹窗」）
  console.log('[空间同步] 发送方广播:', { from: tabId, identityId, teamName });
  const message: SpaceSwitchMessage = {
    type: 'space_switched',
    from: tabId,
    identityId,
    teamName,
  };
  worker.port.postMessage(message);
  // 兜底通道：同内容落 localStorage，其他标签经 storage 事件接收
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...message, ts: Date.now() }));
  } catch (error) {
    console.warn('[空间同步] 兜底 localStorage 写入失败（隐私模式等）:', error);
  }
};

/** 订阅他标签的空间切换消息（自身发送的回投已过滤）；返回退订函数 */
export const subscribeSpaceSwitch = (
  handler: (msg: SpaceSwitchMessage) => void
): (() => void) => {
  const worker = getWorker();
  if (!worker) {
    console.warn('[空间同步] 接收方无 SharedWorker，本标签不接收切换广播');
    return () => {};
  }
  console.log('[空间同步] 本标签订阅切换广播, tabId:', tabId);
  const dispatch = (msg: SpaceSwitchMessage) => {
    if (msg.from === tabId) {
      console.log('[空间同步] 收到自己标签的广播，忽略:', msg.identityId);
      return;
    }
    handler(msg);
  };
  const listener = (ev: MessageEvent) => {
    const msg = ev.data as SpaceSwitchMessage | undefined;
    if (!msg || msg.type !== 'space_switched') return;
    console.log('[空间同步] 收到他标签广播:', msg.identityId, 'from:', msg.from);
    dispatch(msg);
  };
  // storage 兜底：他标签写 localStorage 时本标签触发（写入方自身不触发）
  const storageListener = (ev: StorageEvent) => {
    if (ev.key !== STORAGE_KEY || !ev.newValue) return;
    try {
      const msg = JSON.parse(ev.newValue) as SpaceSwitchMessage;
      if (msg.type !== 'space_switched') return;
      console.log('[空间同步] 收到 storage 兜底广播:', msg.identityId, 'from:', msg.from);
      dispatch(msg);
    } catch {
      // 半截写入/脏数据，忽略
    }
  };
  worker.port.addEventListener('message', listener);
  window.addEventListener('storage', storageListener);
  worker.port.start();
  return () => {
    console.log('[空间同步] 本标签退订切换广播, tabId:', tabId);
    worker.port.removeEventListener('message', listener);
    window.removeEventListener('storage', storageListener);
  };
};
